import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  DatabaseBackup, Download, Trash2, RefreshCw, Play, FlaskConical, HardDrive, Cloud,
  ShieldCheck, KeyRound, Clock, CheckCircle2, XCircle, AlertTriangle, ScrollText,
  Loader2, Copy, Info, FolderCog, Star,
} from "lucide-react";
import { http } from "@/lib/api";
import { TextField, SwitchField, SelectField, TextareaField } from "@/components/kit/fields";
import { SearchSelect } from "@/components/kit/SearchSelect";
import { TIMEZONES } from "@/lib/timezones";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { fmtDate } from "@/lib/format";
import { cn } from "@/lib/utils";

const fmtBytes = (b) => {
  if (b == null) return "—";
  if (b === 0) return "0 B";
  const u = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.min(Math.floor(Math.log(b) / Math.log(1024)), u.length - 1);
  return `${(b / 1024 ** i).toFixed(i ? 1 : 0)} ${u[i]}`;
};
const STORAGE_LABELS = { local: "Local", r2: "Cloudflare R2", gdrive: "Google Drive" };
const STATUS_UI = {
  success: { c: "#22C55E", icon: CheckCircle2, label: "Success" },
  partial: { c: "#EAB308", icon: AlertTriangle, label: "Partial" },
  failed: { c: "#EF4444", icon: XCircle, label: "Failed" },
  running: { c: "#38BDF8", icon: Loader2, label: "Running" },
};

function Panel({ title, children, testId, right, icon: Icon }) {
  return (
    <div className="rounded-xl border border-white/[0.08] bg-[#141417] p-5" data-testid={testId}>
      <div className="mb-4 flex items-center justify-between">
        <h3 className="flex items-center gap-2 text-sm font-medium text-foreground">{Icon && <Icon className="h-4 w-4 text-muted-foreground" />}{title}</h3>
        {right}
      </div>
      {children}
    </div>
  );
}

function Stat({ label, value, sub, testId }) {
  return (
    <div className="rounded-lg border border-white/[0.06] bg-[#0A0A0A] p-3.5" data-testid={testId}>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 truncate text-sm font-semibold text-foreground">{value ?? "—"}</div>
      {sub && <div className="mt-0.5 truncate text-xs text-muted-foreground/70">{sub}</div>}
    </div>
  );
}

function BStatus({ status }) {
  const s = STATUS_UI[status] || { c: "#A1A1AA", icon: Info, label: status };
  const I = s.icon;
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium" style={{ color: s.c, backgroundColor: `${s.c}1a` }}>
      <I className={cn("h-3 w-3", status === "running" && "animate-spin")} />{s.label}
    </span>
  );
}

function StorageChips({ storages }) {
  const entries = Object.entries(storages || {});
  if (!entries.length) return <span className="text-xs text-muted-foreground">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {entries.map(([name, r]) => {
        const ok = r.status === "success" || r.status === "verified_removed";
        const neutral = r.status === "removed_by_retention";
        return (
          <span key={name} title={r.error || (neutral ? "Removed by retention policy" : "")} className={cn("rounded px-1.5 py-0.5 text-[10px] font-medium",
            ok ? "bg-emerald-500/10 text-emerald-400" : neutral ? "bg-white/[0.06] text-muted-foreground line-through" : r.status === "pending" ? "bg-sky-500/10 text-sky-400" : "bg-red-500/10 text-red-400")}>
            {STORAGE_LABELS[name] || name}{r.status === "failed" && " ✕"}
          </span>
        );
      })}
    </div>
  );
}

function OneTimeKeyDialog({ keyValue, onClose }) {
  return (
    <Dialog open={!!keyValue} onOpenChange={(o) => !o && onClose()}>
      <DialogContent aria-describedby={undefined} className="border-white/[0.1] bg-[#141417] sm:max-w-lg" data-testid="backup-key-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2 text-foreground"><KeyRound className="h-4 w-4 text-amber-400" /> Encryption key — save it now</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">This key is shown <b className="text-foreground">only once</b>. You will need it to restore backups via <code>restore.sh</code>. Store it in a safe place (password manager).</p>
        <div className="flex items-center gap-2 rounded-lg border border-amber-400/30 bg-amber-400/5 p-3">
          <code className="flex-1 break-all text-xs text-amber-300" data-testid="backup-enc-key">{keyValue}</code>
          <Button size="sm" variant="ghost" data-testid="copy-enc-key" onClick={() => { navigator.clipboard.writeText(keyValue); toast.success("Key copied"); }}><Copy className="h-4 w-4" /></Button>
        </div>
        <DialogFooter><Button data-testid="close-key-dialog" onClick={onClose}>I saved the key</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StorageCard({ name, cfg, primary, onSetPrimary, onSaved }) {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState("");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const merged = { ...cfg, ...form };
  const save = async (extra = {}) => {
    setBusy("save");
    try { await http.put(`/system/backups/storage/${name}`, { ...form, ...extra }); setForm({}); onSaved(); toast.success(`${STORAGE_LABELS[name]} settings saved`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(""); }
  };
  const test = async () => {
    setBusy("test");
    try {
      const { data } = await http.post(`/system/backups/storage/${name}/test`);
      data.ok ? toast.success(data.message) : toast.error(data.message);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Test failed"); }
    finally { setBusy(""); }
  };
  const connected = cfg.status === "connected";
  const statusLabel = name === "local" ? "Connected" : connected ? "Connected" : cfg.status === "error" ? "Error" : "Disconnected";
  const statusColor = name === "local" || connected ? "#22C55E" : cfg.status === "error" ? "#EF4444" : "#71717A";
  const Icon = name === "local" ? HardDrive : Cloud;
  return (
    <div className="rounded-xl border border-white/[0.08] bg-[#141417] p-5" data-testid={`storage-card-${name}`}>
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium text-foreground">{STORAGE_LABELS[name]}</span>
          {primary === name && <span className="inline-flex items-center gap-1 rounded-full bg-sky-500/10 px-2 py-0.5 text-[10px] font-medium text-sky-400"><Star className="h-2.5 w-2.5" /> Primary</span>}
        </div>
        <span className="inline-flex items-center gap-1.5 text-xs font-medium" style={{ color: statusColor }}>
          <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: statusColor }} />{statusLabel}
        </span>
      </div>
      <div className="space-y-3">
        <SwitchField label="Enabled" checked={merged.enabled} testId={`storage-${name}-enabled`}
          onChange={async (v) => { set("enabled", v); setBusy("save"); try { await http.put(`/system/backups/storage/${name}`, { enabled: v }); onSaved(); } finally { setBusy(""); } }} />
        {name === "local" && (<>
          <TextField label="Folder path" value={merged.folder_path} onChange={(v) => set("folder_path", v)} testId="local-folder" />
          <TextField label="Keep last local backups" type="number" value={merged.keep_last} onChange={(v) => set("keep_last", Number(v))} testId="local-keep-last"
            hint="How many recent backups to keep on the server. Older local copies are deleted automatically." />
        </>)}
        {name === "r2" && (<>
          <TextField label="Bucket" value={merged.bucket} onChange={(v) => set("bucket", v)} testId="r2-bucket" placeholder="my-backups" />
          <TextField label="Endpoint" value={merged.endpoint} onChange={(v) => set("endpoint", v)} testId="r2-endpoint" placeholder="https://<account-id>.r2.cloudflarestorage.com" />
          <TextField label="Access Key" value={form.access_key ?? ""} onChange={(v) => set("access_key", v)} testId="r2-access-key" placeholder={cfg.access_key_mask || "Access key ID"} />
          <TextField label="Secret Key" type="password" value={form.secret_key ?? ""} onChange={(v) => set("secret_key", v)} testId="r2-secret-key" placeholder={cfg.secret_key_mask || "Secret access key"} />
          <TextField label="Keep last R2 backups" type="number" value={merged.keep_last} onChange={(v) => set("keep_last", Number(v))} testId="r2-keep-last"
            hint="How many recent backups to keep in Cloudflare R2. Does not affect Local or Google Drive." />
        </>)}
        {name === "gdrive" && (<>
          <TextareaField label="Service Account JSON" rows={3} value={form.sa_json ?? ""} onChange={(v) => set("sa_json", v)} testId="gdrive-sa-json"
            hint={cfg.sa_email ? `Connected account: ${cfg.sa_email}` : "Paste the full JSON key of your Google Cloud service account"} />
          <TextField label="Folder ID" value={merged.folder_id} onChange={(v) => set("folder_id", v)} testId="gdrive-folder" placeholder="Drive folder ID (share the folder with the service account email)" />
          <TextField label="Keep last Google Drive backups" type="number" value={merged.keep_last} onChange={(v) => set("keep_last", Number(v))} testId="gdrive-keep-last"
            hint="How many recent backups to keep in Google Drive. Does not affect Local or Cloudflare R2." />
        </>)}
        {cfg.last_upload_at && <div className="text-xs text-muted-foreground">Last upload: {fmtDate(cfg.last_upload_at)}</div>}
        {cfg.last_error && <div className="break-all text-xs text-red-400">Last error: {cfg.last_error}</div>}
        <div className="flex flex-wrap gap-2 pt-1">
          <Button size="sm" variant="secondary" data-testid={`storage-${name}-save`} disabled={busy === "save" || !Object.keys(form).length} onClick={() => save()}>Save</Button>
          <Button size="sm" variant="outline" data-testid={`storage-${name}-test`} disabled={busy === "test"} onClick={test}>
            {busy === "test" ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : null}Test connection
          </Button>
          {primary !== name && <Button size="sm" variant="ghost" data-testid={`storage-${name}-primary`} onClick={() => onSetPrimary(name)}>Set as Primary</Button>}
        </div>
      </div>
    </div>
  );
}

const FREQ_OPTS = [
  { value: "disabled", label: "Disabled" },
  { value: "int-1", label: "Every hour" },
  { value: "int-2", label: "Every 2 hours" },
  { value: "int-3", label: "Every 3 hours" },
  { value: "int-4", label: "Every 4 hours" },
  { value: "int-6", label: "Every 6 hours" },
  { value: "int-8", label: "Every 8 hours" },
  { value: "int-12", label: "Every 12 hours" },
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "cron", label: "Custom cron" },
];

// Human preview of interval runs: anchor HH:MM defines the grid (HH % n, then every n hours).
function intervalRuns(sch) {
  const n = Number(sch.every_hours || 1);
  const [hh, mm] = (sch.time || "02:00").split(":").map((x) => parseInt(x, 10));
  const m = String(Number.isNaN(mm) ? 0 : mm).padStart(2, "0");
  if (n <= 1) return `Runs every hour at minute :${m}`;
  const out = [];
  for (let h = (Number.isNaN(hh) ? 2 : hh) % n; h < 24; h += n) out.push(`${String(h).padStart(2, "0")}:${m}`);
  return `Runs daily at ${out.join(", ")}`;
}

function SchedulePanel({ settings, onSaved }) {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const sch = form || settings.schedule;
  const save = async () => {
    setBusy(true);
    try { await http.put("/system/backups/settings", { schedule: sch }); setForm(null); onSaved(); toast.success("Schedule saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  return (
    <Panel title="Backup Schedule" icon={Clock} testId="backup-schedule-panel"
      right={<Button size="sm" data-testid="save-schedule" disabled={busy || !form} onClick={save}>Save</Button>}>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <SelectField label="Frequency" value={sch.mode === "interval" ? `int-${sch.every_hours || 1}` : sch.mode === "hourly" ? "int-1" : sch.mode} testId="schedule-mode"
          onChange={(v) => (v.startsWith("int-") ? setForm({ ...sch, mode: "interval", every_hours: Number(v.slice(4)) }) : setForm({ ...sch, mode: v }))}
          options={FREQ_OPTS} />
        {["daily", "weekly", "monthly", "hourly", "interval"].includes(sch.mode) && (
          <TextField label={["hourly", "interval"].includes(sch.mode) ? "Anchor time (HH:MM)" : "Start time"} value={sch.time} onChange={(v) => setForm({ ...sch, time: v })} testId="schedule-time" placeholder="02:00"
            hint={["hourly", "interval"].includes(sch.mode) ? intervalRuns(sch) : undefined} />)}
        {sch.mode === "weekly" && (
          <SelectField label="Day of week" value={String(sch.weekday ?? 0)} testId="schedule-weekday" onChange={(v) => setForm({ ...sch, weekday: Number(v) })}
            options={["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"].map((d, i) => ({ value: String(i), label: d }))} />)}
        {sch.mode === "monthly" && (
          <TextField label="Day of month" type="number" value={sch.day ?? 1} onChange={(v) => setForm({ ...sch, day: Number(v) })} testId="schedule-day" />)}
        {sch.mode === "cron" && (
          <TextField label="Cron expression" value={sch.cron} onChange={(v) => setForm({ ...sch, cron: v })} testId="schedule-cron" placeholder="0 2 * * *" hint="min hour day month weekday" />)}
        {sch.mode !== "disabled" && (
          <div className="space-y-1.5">
            <div className="text-xs font-medium text-muted-foreground">Timezone</div>
            <SearchSelect value={sch.timezone || "UTC"} onChange={(v) => setForm({ ...sch, timezone: v })}
              options={TIMEZONES} testId="schedule-timezone" widthClass="w-full" />
          </div>)}
      </div>
      <p className="mt-3 text-xs text-muted-foreground/70">How many copies are kept is configured per storage above (Keep last … backups).</p>
    </Panel>
  );
}

function EncryptionPanel({ settings, onSaved }) {
  const [newKey, setNewKey] = useState("");
  const [busy, setBusy] = useState(false);
  const enc = settings.encryption;
  const act = async (path, label) => {
    setBusy(true);
    try {
      const { data } = await http.post(`/system/backups/encryption/${path}`);
      if (data.key) setNewKey(data.key);
      onSaved(); toast.success(label);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Panel title="Encryption" icon={ShieldCheck} testId="backup-encryption-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-sm text-foreground">
            {enc.enabled ? <CheckCircle2 className="h-4 w-4 text-emerald-400" /> : <XCircle className="h-4 w-4 text-muted-foreground" />}
            {enc.enabled ? "Encryption enabled" : "Encryption disabled"}
            {enc.enabled && enc.key_mask && <code className="rounded bg-white/[0.06] px-1.5 py-0.5 text-xs text-muted-foreground">{enc.key_mask}</code>}
          </div>
          <p className="mt-1 text-xs text-muted-foreground">Backups are stored as encrypted archives. The key is shown once — keep it safe, it is required for restore.</p>
        </div>
        <div className="flex gap-2">
          {!enc.enabled ? (
            <Button size="sm" data-testid="enc-enable" disabled={busy} onClick={() => act("enable", "Encryption enabled")}>Enable encryption</Button>
          ) : (<>
            <Button size="sm" variant="outline" data-testid="enc-rotate" disabled={busy} onClick={() => act("rotate", "Key rotated — old backups still use the old key")}>Rotate key</Button>
            <Button size="sm" variant="ghost" className="text-red-400" data-testid="enc-disable" disabled={busy} onClick={() => act("disable", "Encryption disabled for new backups")}>Disable</Button>
          </>)}
        </div>
      </div>
      <OneTimeKeyDialog keyValue={newKey} onClose={() => setNewKey("")} />
    </Panel>
  );
}

export default function BackupsTab() {
  const qc = useQueryClient();
  const [selected, setSelected] = useState({});
  const [logsFor, setLogsFor] = useState(null);
  const [detailsFor, setDetailsFor] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [busy, setBusy] = useState("");

  const { data: overview } = useQuery({ queryKey: ["backups-overview"], queryFn: async () => (await http.get("/system/backups/overview")).data, refetchInterval: (q) => (q.state.data?.running ? 3000 : 15000) });
  const { data: settings } = useQuery({ queryKey: ["backups-settings"], queryFn: async () => (await http.get("/system/backups/settings")).data });
  const { data: list } = useQuery({ queryKey: ["backups-list"], queryFn: async () => (await http.get("/system/backups")).data, refetchInterval: overview?.running ? 3000 : 15000 });
  const { data: logs } = useQuery({ queryKey: ["backup-logs", logsFor?.id], enabled: !!logsFor, queryFn: async () => (await http.get(`/system/backups/${logsFor.id}/logs`)).data, refetchInterval: logsFor && overview?.running ? 2000 : false });

  const refresh = () => { qc.invalidateQueries({ queryKey: ["backups-overview"] }); qc.invalidateQueries({ queryKey: ["backups-list"] }); qc.invalidateQueries({ queryKey: ["backups-settings"] }); };

  const run = async (path, label) => {
    setBusy(path);
    try { await http.post(`/system/backups/${path}`); toast.success(label); setTimeout(refresh, 500); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to start"); }
    finally { setBusy(""); }
  };
  const download = async (b) => {
    setBusy(`dl-${b.id}`);
    try {
      const res = await http.get(`/system/backups/${b.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = b.filename; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("No downloadable copy available"); }
    finally { setBusy(""); }
  };
  const doDelete = async (ids) => {
    setBusy("del");
    try {
      for (const id of ids) await http.delete(`/system/backups/${id}`);
      toast.success(`Deleted ${ids.length} backup(s)`); setSelected({}); refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
    finally { setBusy(""); setConfirmDel(null); }
  };
  const setPrimary = async (name) => {
    await http.put("/system/backups/settings", { primary: name });
    qc.invalidateQueries({ queryKey: ["backups-settings"] }); toast.success(`Primary storage: ${STORAGE_LABELS[name]}`);
  };

  if (!overview || !settings) return null;
  const items = list?.items || [];
  const latest = items.find((b) => ["success", "partial"].includes(b.status) && b.type !== "test");
  const selectedIds = Object.keys(selected).filter((k) => selected[k]);
  const u = overview.storage_usage;

  return (
    <div className="space-y-5" data-testid="backups-tab">
      {/* Actions */}
      <div className="flex flex-wrap items-center gap-2">
        <Button data-testid="create-backup-now" disabled={overview.running || busy === "run"} onClick={() => run("run", "Backup started in background")}>
          {overview.running ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Play className="mr-1.5 h-4 w-4" />}{overview.running ? "Backup running…" : "Create backup now"}
        </Button>
        <Button variant="secondary" data-testid="run-test-backup" disabled={overview.running || busy === "run-test"} onClick={() => run("run-test", "Test backup started — full pipeline check")}>
          <FlaskConical className="mr-1.5 h-4 w-4" />Run test backup
        </Button>
        <Button variant="outline" data-testid="download-latest" disabled={!latest || !!busy} onClick={() => latest && download(latest)}>
          <Download className="mr-1.5 h-4 w-4" />Download latest
        </Button>
        {selectedIds.length > 0 && (
          <Button variant="outline" className="text-red-400" data-testid="delete-selected" disabled={busy === "del"} onClick={() => setConfirmDel(selectedIds)}>
            <Trash2 className="mr-1.5 h-4 w-4" />Delete selected ({selectedIds.length})
          </Button>
        )}
        <Button variant="ghost" data-testid="backups-refresh" onClick={refresh}><RefreshCw className="h-4 w-4" /></Button>
      </div>

      {/* Dashboard */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat testId="stat-last-success" label="Last successful backup"
          value={overview.last_success ? fmtDate(overview.last_success.started_at) : "Never"}
          sub={overview.last_success ? `${fmtBytes(overview.last_success.size)} · ${overview.last_success.duration_sec}s` : "Run your first backup"} />
        <Stat testId="stat-last-failed" label="Last failed backup"
          value={overview.last_failed ? fmtDate(overview.last_failed.started_at) : "None"}
          sub={overview.last_failed?.error?.slice(0, 60)} />
        <Stat testId="stat-schedule" label="Schedule"
          value={overview.schedule.mode === "disabled" ? "Disabled"
            : overview.schedule.mode === "cron" ? overview.schedule.cron
            : overview.schedule.mode === "interval" ? `Every ${overview.schedule.every_hours || 1}h · ${overview.schedule.time}`
            : overview.schedule.mode === "hourly" ? `Every 1h · ${overview.schedule.time}`
            : `${overview.schedule.mode} · ${overview.schedule.time}`}
          sub={`${overview.schedule.mode !== "disabled" ? `${overview.schedule.timezone || "UTC"} · ` : ""}Encryption: ${overview.encryption_enabled ? "enabled" : "off"} · Mongo ${overview.mongo_version}`} />
        <Stat testId="stat-storage-usage" label="Storage usage"
          value={`${fmtBytes(u.local_bytes)} local`}
          sub={`All copies: ${fmtBytes(u.all_backups_bytes)} · Disk free: ${fmtBytes(u.disk_free_bytes)}`} />
      </div>

      {/* History table */}
      <Panel title="Backup History" icon={DatabaseBackup} testId="backup-history-panel">
        {items.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground">No backups yet. Click “Create backup now” to make the first one.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/[0.06] text-left text-xs text-muted-foreground">
                  <th className="w-8 pb-2"><input type="checkbox" data-testid="select-all-backups" className="accent-sky-500"
                    checked={items.length > 0 && selectedIds.length === items.filter((b) => b.status !== "running").length}
                    onChange={(e) => setSelected(e.target.checked ? Object.fromEntries(items.filter((b) => b.status !== "running").map((b) => [b.id, true])) : {})} /></th>
                  <th className="pb-2">Date</th><th className="pb-2">Type</th><th className="pb-2">Status</th>
                  <th className="pb-2">Size</th><th className="pb-2">SHA-256</th><th className="pb-2">Storages</th><th className="pb-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((b) => (
                  <tr key={b.id} className="border-b border-white/[0.04] last:border-0" data-testid={`backup-row-${b.id}`}>
                    <td className="py-2.5"><input type="checkbox" className="accent-sky-500" disabled={b.status === "running"} checked={!!selected[b.id]} onChange={(e) => setSelected((s) => ({ ...s, [b.id]: e.target.checked }))} /></td>
                    <td className="py-2.5 text-foreground">{fmtDate(b.started_at)}</td>
                    <td className="py-2.5"><span className={cn("rounded px-1.5 py-0.5 text-[10px] font-medium uppercase", b.type === "automatic" ? "bg-sky-500/10 text-sky-400" : b.type === "test" ? "bg-purple-500/10 text-purple-400" : "bg-white/[0.06] text-muted-foreground")}>{b.type}</span></td>
                    <td className="py-2.5"><BStatus status={b.status} /></td>
                    <td className="py-2.5 text-muted-foreground">{fmtBytes(b.size)}</td>
                    <td className="py-2.5">{b.sha256 ? <code className="text-xs text-muted-foreground" title={b.sha256}>{b.sha256.slice(0, 10)}…</code> : "—"}</td>
                    <td className="py-2.5"><StorageChips storages={b.storages} /></td>
                    <td className="py-2.5">
                      <div className="flex justify-end gap-1">
                        {["success", "partial"].includes(b.status) && b.type !== "test" && (
                          <Button size="icon" variant="ghost" className="h-7 w-7" title="Download" data-testid={`download-${b.id}`} disabled={busy === `dl-${b.id}`} onClick={() => download(b)}>
                            {busy === `dl-${b.id}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                          </Button>)}
                        <Button size="icon" variant="ghost" className="h-7 w-7" title="Logs" data-testid={`logs-${b.id}`} onClick={() => setLogsFor(b)}><ScrollText className="h-3.5 w-3.5" /></Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7" title="Details" data-testid={`details-${b.id}`} onClick={() => setDetailsFor(b)}><Info className="h-3.5 w-3.5" /></Button>
                        {b.status !== "running" && <Button size="icon" variant="ghost" className="h-7 w-7 text-red-400" title="Delete" data-testid={`delete-${b.id}`} onClick={() => setConfirmDel([b.id])}><Trash2 className="h-3.5 w-3.5" /></Button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {/* Storage providers */}
      <div>
        <div className="mb-3 flex items-center gap-2 text-sm font-medium text-foreground"><FolderCog className="h-4 w-4 text-muted-foreground" /> Storage Providers</div>
        <div className="grid gap-4 lg:grid-cols-3">
          {["local", "r2", "gdrive"].map((name) => (
            <StorageCard key={name} name={name} cfg={settings.storages[name]} primary={settings.primary}
              onSetPrimary={setPrimary} onSaved={refresh} />
          ))}
        </div>
      </div>

      <SchedulePanel settings={settings} onSaved={refresh} />
      <EncryptionPanel settings={settings} onSaved={refresh} />

      <div className="flex items-start gap-2 rounded-lg border border-white/[0.06] bg-[#0A0A0A] p-3 text-xs text-muted-foreground">
        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        Restore is intentionally CLI-only for safety. On the server: <code className="mx-1 rounded bg-white/[0.06] px-1">docker compose cp backup-file.tar.enc backend:/tmp/ && docker compose exec backend bash /app/scripts/restore.sh /tmp/backup-file.tar.enc --key &lt;encryption-key&gt;</code>
      </div>

      {/* Logs dialog */}
      <Dialog open={!!logsFor} onOpenChange={(o) => !o && setLogsFor(null)}>
        <DialogContent aria-describedby={undefined} className="border-white/[0.1] bg-[#141417] sm:max-w-2xl" data-testid="backup-logs-dialog">
          <DialogHeader><DialogTitle className="text-foreground">Backup Logs</DialogTitle></DialogHeader>
          <div className="max-h-96 space-y-1 overflow-y-auto rounded-lg bg-[#0A0A0A] p-3 font-mono text-xs">
            {(logs?.items || []).map((l) => (
              <div key={l.id} className={cn(l.level === "error" ? "text-red-400" : l.level === "warn" ? "text-amber-400" : "text-muted-foreground")}>
                <span className="text-muted-foreground/50">{l.ts?.slice(11, 19)}</span> {l.message}
              </div>
            ))}
            {!(logs?.items || []).length && <div className="text-muted-foreground">No logs</div>}
          </div>
        </DialogContent>
      </Dialog>

      {/* Details dialog */}
      <Dialog open={!!detailsFor} onOpenChange={(o) => !o && setDetailsFor(null)}>
        <DialogContent aria-describedby={undefined} className="border-white/[0.1] bg-[#141417] sm:max-w-lg" data-testid="backup-details-dialog">
          <DialogHeader><DialogTitle className="text-foreground">Backup Details</DialogTitle></DialogHeader>
          {detailsFor && (
            <div className="space-y-2 text-sm">
              {[["File", detailsFor.filename], ["Started", fmtDate(detailsFor.started_at)], ["Finished", detailsFor.finished_at ? fmtDate(detailsFor.finished_at) : "—"],
                ["Duration", detailsFor.duration_sec != null ? `${detailsFor.duration_sec}s` : "—"], ["Size", fmtBytes(detailsFor.size)],
                ["Encrypted", detailsFor.encrypted ? "Yes" : "No"], ["Backup version", detailsFor.backup_version], ["Triggered by", detailsFor.triggered_by]].map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4 border-b border-white/[0.04] pb-1.5 last:border-0"><span className="text-muted-foreground">{k}</span><span className="text-right text-foreground">{v}</span></div>
              ))}
              <div>
                <div className="mb-1 text-muted-foreground">SHA-256 (Integrity {detailsFor.sha256 ? "verified ✓" : "—"})</div>
                {detailsFor.sha256 && <code className="break-all text-xs text-emerald-400">{detailsFor.sha256}</code>}
              </div>
              <div>
                <div className="mb-1.5 text-muted-foreground">Storage results</div>
                {Object.entries(detailsFor.storages || {}).map(([n, r]) => (
                  <div key={n} className="flex items-center justify-between py-0.5 text-xs">
                    <span className="text-foreground">{STORAGE_LABELS[n] || n}</span>
                    <span className={r.status === "failed" ? "text-red-400" : "text-emerald-400"}>{r.status}{r.error ? ` — ${r.error.slice(0, 80)}` : ""}</span>
                  </div>
                ))}
              </div>
              {detailsFor.error && <div className="break-all rounded bg-red-500/10 p-2 text-xs text-red-400">{detailsFor.error}</div>}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <DialogContent aria-describedby={undefined} className="border-white/[0.1] bg-[#141417] sm:max-w-md" data-testid="backup-delete-dialog">
          <DialogHeader><DialogTitle className="text-foreground">Delete backup{confirmDel?.length > 1 ? "s" : ""}?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">This removes {confirmDel?.length} backup(s) from all storages permanently. This cannot be undone.</p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmDel(null)}>Cancel</Button>
            <Button variant="destructive" data-testid="confirm-delete-backups" disabled={busy === "del"} onClick={() => doDelete(confirmDel)}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
